// src/admin/pages/AdminNewsletter.js
import React, { useState, useEffect, useCallback } from 'react';
import { showToast } from '../../contexts/ToastNotification';
import axios from 'axios';
import './admin-newsletter.scss';

const API_URL = process.env.REACT_APP_API_URL || '/api';

function AdminNewsletter() {
    const [subscribers, setSubscribers] = useState([]);
    const [loading, setLoading] = useState(true);
    //const [showModal, setShowModal] = useState(false);
    //const [selectedSubscriber, setSelectedSubscriber] = useState(null);
    const [campaigns, setCampaigns] = useState([]);
    const [showCampaignModal, setShowCampaignModal] = useState(false);
    const [campaignForm, setCampaignForm] = useState({
        subject: '',
        content: '',
        sendTo: 'all',
        scheduledDate: ''
    });
    const [filters, setFilters] = useState({ search: '', status: '' });
    const [pagination, setPagination] = useState({ page: 1, total: 0, totalPages: 1 });
    const [stats, setStats] = useState({
        total: 0,
        active: 0,
        unsubscribed: 0,
        campaignsSent: 0,
        openRate: 0,
        clickRate: 0
    });

    // Carica iscritti
    const loadSubscribers = useCallback(async () => {
        setLoading(true);
        try {
            let url = `${API_URL}/admin/newsletter/subscribers?page=${pagination.page}&limit=20`;
            if (filters.search) url += `&search=${encodeURIComponent(filters.search)}`;
            if (filters.status) url += `&status=${filters.status}`;
            
            const response = await axios.get(url, { withCredentials: true });
            const data = response.data;
            
            if (data.success) {
                setSubscribers(data.subscribers);
                setPagination(prev => ({
                    ...prev,
                    total: data.pagination.total,
                    totalPages: data.pagination.totalPages
                }));
            } else {
                // Dati di esempio
                setSubscribers([
                    { id: 1, email: 'mario.rossi@example.com', nome: 'Mario', cognome: 'Rossi', iscritto: true, data_iscrizione: '2024-01-15', ultima_attivita: '2024-12-01' },
                    { id: 2, email: 'laura.bianchi@example.com', nome: 'Laura', cognome: 'Bianchi', iscritto: true, data_iscrizione: '2024-02-20', ultima_attivita: '2024-12-01' },
                    { id: 3, email: 'giuseppe.verdi@example.com', nome: 'Giuseppe', cognome: 'Verdi', iscritto: false, data_iscrizione: '2024-03-10', data_disiscrizione: '2024-11-15' }
                ]);
                setStats({
                    total: 342,
                    active: 328,
                    unsubscribed: 14,
                    campaignsSent: 12,
                    openRate: 68,
                    clickRate: 23
                });
                setCampaigns([
                    { id: 1, subject: 'Novità sui vini d\'Abruzzo', sentDate: '2024-11-25', sentTo: 342, opened: 232, clicked: 78, status: 'sent' },
                    { id: 2, subject: 'Offerta Speciale Natale', sentDate: '2024-12-01', sentTo: 328, opened: 245, clicked: 92, status: 'sent' }
                ]);
            }
        } catch (error) {
            console.error('Error loading subscribers:', error);
            showToast('Errore di connessione', 'error');
        } finally {
            setLoading(false);
        }
    }, [pagination.page, filters.search, filters.status]);

    useEffect(() => {
        loadSubscribers();
    }, [loadSubscribers]);

    // Invia campagna newsletter
    const sendCampaign = async () => {
        if (!campaignForm.subject || !campaignForm.content) {
            showToast('Compila soggetto e contenuto', 'warning');
            return;
        }

        try {
            const response = await axios.post(
                `${API_URL}/admin/newsletter/send`,
                campaignForm,
                { withCredentials: true }
            );
            
            const data = response.data;
            
            if (data.success) {
                showToast(`Newsletter inviata a ${data.sentCount} iscritti`, 'success');
                setShowCampaignModal(false);
                setCampaignForm({ subject: '', content: '', sendTo: 'all', scheduledDate: '' });
                loadSubscribers();
            } else {
                showToast(data.message || 'Errore nell\'invio', 'error');
            }
        } catch (error) {
            console.error('Error sending campaign:', error);
            showToast('Errore di connessione', 'error');
        }
    };

    // Esporta iscritti
    const exportSubscribers = async () => {
        try {
            const response = await axios.get(
                `${API_URL}/admin/newsletter/export`,
                { 
                    withCredentials: true,
                    responseType: 'blob' // Importante per il download del file
                }
            );
            
            const blob = response.data;
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `newsletter_subscribers_${new Date().toISOString().split('T')[0]}.csv`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
            showToast('Esportazione completata', 'success');
        } catch (error) {
            console.error('Error exporting:', error);
            showToast('Errore nell\'esportazione', 'error');
        }
    };

    // Gestisci iscrizione/disiscrizione
    const toggleSubscription = async (subscriberId, currentStatus) => {
        try {
            const response = await axios.put(
                `${API_URL}/admin/newsletter/subscribers/${subscriberId}/toggle`,
                { iscritto: !currentStatus },
                { withCredentials: true }
            );
            
            const data = response.data;
            
            if (data.success) {
                showToast(`Utente ${!currentStatus ? 'iscritto' : 'disiscritto'} con successo`, 'success');
                loadSubscribers();
            } else {
                showToast(data.message || 'Errore', 'error');
            }
        } catch (error) {
            console.error('Error toggling subscription:', error);
            showToast('Errore di connessione', 'error');
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/D';
        const date = new Date(dateString);
        return date.toLocaleDateString('it-IT');
    };

    if (loading && subscribers.length === 0) {
        return (
            <div className="admin-loading">
                <div className="loader"></div>
                <span>Caricamento newsletter...</span>
            </div>
        );
    }

    return (
        <div className="admin-newsletter">
            {/* Header */}
            <div className="newsletter-header">
                <div className="header-title">
                    <h2>📧 Newsletter e Comunicazioni</h2>
                    <p>Gestisci iscritti, crea e invia campagne email</p>
                </div>
                <div className="header-actions">
                    <button className="export-btn" onClick={exportSubscribers}>
                        📥 Esporta Iscritti
                    </button>
                    <button className="create-btn" onClick={() => setShowCampaignModal(true)}>
                        ✨ Nuova Campagna
                    </button>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="newsletter-stats">
                <div className="stat-card">
                    <div className="stat-icon">📧</div>
                    <div className="stat-value">{stats.total}</div>
                    <div className="stat-label">Totale Iscritti</div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon">✅</div>
                    <div className="stat-value">{stats.active}</div>
                    <div className="stat-label">Attivi</div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon">📊</div>
                    <div className="stat-value">{stats.campaignsSent}</div>
                    <div className="stat-label">Campagne Inviate</div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon">📈</div>
                    <div className="stat-value">{stats.openRate}%</div>
                    <div className="stat-label">Tasso di Apertura</div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon">🖱️</div>
                    <div className="stat-value">{stats.clickRate}%</div>
                    <div className="stat-label">Tasso di Click</div>
                </div>
            </div>

            {/* Filters */}
            <div className="newsletter-filters">
                <div className="search-wrapper">
                    <span className="search-icon">🔍</span>
                    <input
                        type="text"
                        placeholder="Cerca per email o nome..."
                        value={filters.search}
                        onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value, page: 1 }))}
                        className="search-input"
                    />
                </div>
                <select
                    value={filters.status}
                    onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value, page: 1 }))}
                    className="filter-select"
                >
                    <option value="">📊 Tutti</option>
                    <option value="active">✅ Iscritti</option>
                    <option value="inactive">❌ Disiscritti</option>
                </select>
            </div>

            {/* Subscribers List and Campaigns */}
            <div className="newsletter-two-columns">
                {/* Subscribers Table */}
                <div className="subscribers-section">
                    <h3>👥 Lista Iscritti</h3>
                    <div className="subscribers-table-container">
                        <table className="subscribers-table">
                            <thead>
                                <tr>
                                    <th>Email</th>
                                    <th>Nome</th>
                                    <th>Data Iscrizione</th>
                                    <th>Stato</th>
                                    <th>Azioni</th>
                                </tr>
                            </thead>
                            <tbody>
                                {subscribers.map(sub => (
                                    <tr key={sub.id}>
                                        <td className="subscriber-email">{sub.email}</td>
                                        <td>{sub.nome} {sub.cognome || ''}</td>
                                        <td>{formatDate(sub.data_iscrizione)}</td>
                                        <td>
                                            <span className={`status-badge ${sub.iscritto ? 'active' : 'inactive'}`}>
                                                {sub.iscritto ? '✅ Iscritto' : '❌ Disiscritto'}
                                            </span>
                                        </td>
                                        <td>
                                            <button 
                                                className={`toggle-btn ${sub.iscritto ? 'inactive' : 'active'}`}
                                                onClick={() => toggleSubscription(sub.id, sub.iscritto)}
                                            >
                                                {sub.iscritto ? 'Disiscrivi' : 'Iscrivi'}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {pagination.totalPages > 1 && (
                        <div className="pagination">
                            <button onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))} disabled={pagination.page === 1}>
                                ← Precedente
                            </button>
                            <span>Pagina {pagination.page} di {pagination.totalPages}</span>
                            <button onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))} disabled={pagination.page === pagination.totalPages}>
                                Successiva →
                            </button>
                        </div>
                    )}
                </div>

                {/* Campaign History */}
                <div className="campaigns-section">
                    <h3>📨 Cronologia Campagne</h3>
                    <div className="campaigns-list">
                        {campaigns.map(campaign => (
                            <div key={campaign.id} className="campaign-item">
                                <div className="campaign-header">
                                    <div className="campaign-subject">{campaign.subject}</div>
                                    <div className={`campaign-status ${campaign.status}`}>
                                        {campaign.status === 'sent' ? '✅ Inviata' : '⏳ Bozza'}
                                    </div>
                                </div>
                                <div className="campaign-details">
                                    <span>📅 {formatDate(campaign.sentDate)}</span>
                                    <span>👥 A {campaign.sentTo} iscritti</span>
                                    <span>📖 Aperture: {campaign.opened}</span>
                                    <span>🖱️ Click: {campaign.clicked}</span>
                                </div>
                                <div className="campaign-stats">
                                    <div className="stat-bar">
                                        <div className="stat-bar-label">Aperture</div>
                                        <div className="bar-container">
                                            <div className="bar open-bar" style={{ width: `${(campaign.opened / campaign.sentTo) * 100}%` }}></div>
                                            <span className="bar-value">{Math.round((campaign.opened / campaign.sentTo) * 100)}%</span>
                                        </div>
                                    </div>
                                    <div className="stat-bar">
                                        <div className="stat-bar-label">Click</div>
                                        <div className="bar-container">
                                            <div className="bar click-bar" style={{ width: `${(campaign.clicked / campaign.sentTo) * 100}%` }}></div>
                                            <span className="bar-value">{Math.round((campaign.clicked / campaign.sentTo) * 100)}%</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Campaign Modal */}
            {showCampaignModal && (
                <div className="modal-overlay" onClick={() => setShowCampaignModal(false)}>
                    <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>✨ Nuova Campagna Newsletter</h3>
                            <button className="close-modal" onClick={() => setShowCampaignModal(false)}>✕</button>
                        </div>
                        <div className="modal-body">
                            <div className="form-group">
                                <label>Soggetto *</label>
                                <input
                                    type="text"
                                    value={campaignForm.subject}
                                    onChange={(e) => setCampaignForm({ ...campaignForm, subject: e.target.value })}
                                    placeholder="es. Novità sui vini d'Abruzzo"
                                    className="form-input"
                                />
                            </div>
                            <div className="form-group">
                                <label>Destinatari *</label>
                                <div className="radio-group">
                                    <label>
                                        <input type="radio" value="all" checked={campaignForm.sendTo === 'all'} onChange={(e) => setCampaignForm({ ...campaignForm, sendTo: e.target.value })} />
                                        Tutti gli iscritti
                                    </label>
                                    <label>
                                        <input type="radio" value="active" checked={campaignForm.sendTo === 'active'} onChange={(e) => setCampaignForm({ ...campaignForm, sendTo: e.target.value })} />
                                        Solo attivi
                                    </label>
                                </div>
                            </div>
                            <div className="form-group">
                                <label>Contenuto *</label>
                                <textarea
                                    value={campaignForm.content}
                                    onChange={(e) => setCampaignForm({ ...campaignForm, content: e.target.value })}
                                    placeholder="Scrivi il contenuto della tua newsletter..."
                                    rows={10}
                                    className="form-textarea"
                                ></textarea>
                            </div>
                            <div className="form-group">
                                <label>Programma invio (opzionale)</label>
                                <input
                                    type="datetime-local"
                                    value={campaignForm.scheduledDate}
                                    onChange={(e) => setCampaignForm({ ...campaignForm, scheduledDate: e.target.value })}
                                    className="form-input"
                                />
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="cancel-btn" onClick={() => setShowCampaignModal(false)}>Annulla</button>
                            <button className="send-btn" onClick={sendCampaign}>📨 Invia Ora</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default AdminNewsletter;